import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpEventType,
} from "@angular/common/http";
import { Observable, throwError, Subject, BehaviorSubject, of } from "rxjs";
import { catchError, switchMap, take, filter, mergeMap } from "rxjs/operators";

import { NzMessageService } from "ng-zorro-antd";

import { AuthenticationService } from "@app/_services";
import { UserToken } from "@app/_models";
import { Router } from "@angular/router";
import { LoadingService } from "./loading.service";
// import { filter } from 'minimatch';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private authenticationService: AuthenticationService,
    private messageService: NzMessageService,
    private router: Router,
    private loadingService: LoadingService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => this.handleError(err, request, next))
    );
  }

  private handleError(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.url.includes("refreshtoken") || request.url.includes("login")) {
      if (request.url.includes("refreshtoken")) {
        this.loadingService.removeRequest();
        this.authenticationService.logout();
        this.router.navigate(["/login"]);
      }
      return this.throwError(err);
    }

    if (err.status === 401 || err.status === 403) {
      if (this.refreshTokenInProgress) {
        return this.refreshTokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap(() => next.handle(this.addAuthenticationToken(request)))
        );
      } else {
        this.refreshTokenInProgress = true;
        this.refreshTokenSubject.next(null);

        return this.authenticationService.refreshToken().pipe(
          switchMap((user: UserToken) => {
            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(user);
            return next.handle(this.addAuthenticationToken(request));
          }),
          catchError(err => {
            this.loadingService.removeRequest();  // Clear loading message on token refresh failure
            this.authenticationService.logout();
            this.router.navigate(["/login"]);
            return this.throwError(err);
          })
        );
      }
    }

    return this.throwError(err);
  }

  private throwError(err: HttpErrorResponse): Observable<never> {
    let errorText = "Greška";
    if (err.error) {
      if (err.error.message) {
        errorText = err.error.message;
      } else if (err.error.Message) {
        errorText = err.error.Message.split("Parameter name")[0];
      } else if (err.error[0] && err.error[0].description) {
        errorText = err.error[0].description;
      } else if (err.error.errors && err.error.errors.length) {
        errorText = err.error.errors[0];
      }
    } else if (err.statusText) {
      errorText = err.statusText;
    }

    if (err.status !== 401 && err.status !== 403) {
      this.messageService.create("error", errorText);
    }
    if (err.status === 500) {
      this.router.navigate(["/error"]);
    }
    this.loadingService.removeRequest();

    return throwError(() => err);
  }

  private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    const currentUserTokenValue = this.authenticationService.currentUserTokenValue;
    const accessToken = currentUserTokenValue && currentUserTokenValue.accessToken.token;

    if (!accessToken) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }
}

